import { ApolloQueryResult, useLazyQuery } from "@apollo/client";
import equal from "deep-equal";
import { ComponentProps, useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { FormattedMessage, useIntl } from "react-intl";
import { useLocation } from "react-router";

import TertiaryButton from "@components/buttons/TertiaryButton";
import styles from "@components/expert/ExpertDashboardMetricPage.module.css";
import ExpertKPIsFilters from "@components/forms/ExpertKPIsFilters";
import HandleQuery from "@components/HandleQuery";
import AppPage from "@components/pageTypes/AppPage";
import Icon from "@components/text/Icon";
import PageTitle from "@components/text/PageTitle";
import TimeLogTables from "@components/TimeLogTables";
import ButtonLink from "@components/typography/ButtonLink";
import useMyExpert from "@hooks/useMyExpert";
import useNodesFromConnection from "@hooks/useNodesFromConnection";
import { TimePeriodEnum } from "@typing/Enums";
import {
  ExpertGlobalRoleEnum,
  GetExpertOverviewDocument,
  GetExpertOverviewQuery,
  GetExpertOverviewQueryVariables,
  GetTimeLogsByGroupDocument,
  GetTimeLogsByGroupQuery,
  GetTimeLogsByGroupQueryVariables,
  TimeLogBillableStatusEnum
} from "@typing/Generated";
import { calculateDatesFromTimePeriod } from "@utils/modelUtils";
import { minutesToHoursAndMinutes } from "@utils/timeUtils";

type Filters = ComponentProps<typeof ExpertKPIsFilters>["filters"];

type TimeLogsData = NonNullable<GetTimeLogsByGroupQuery["carePlan"]>;

enum Type {
  BILLABLE = "billable",
  NON_BILLABLE = "non_billable"
}

type LoadMoreArgs = {
  variables: {
    cursor: string;
  };
};

type SectionProps = {
  data: TimeLogsData;
  fetchMore: (args: LoadMoreArgs) => Promise<ApolloQueryResult<GetTimeLogsByGroupQuery>>;
  refetch: () => void;
  tableIndex: number;
  totalMinutes: number;
};

const Section = ({ data, fetchMore, refetch, tableIndex, totalMinutes }: SectionProps) => {
  const timeLogs = useNodesFromConnection(data);
  const [loading, setLoading] = useState(false);

  const searchNext = () => {
    setLoading(true);
    fetchMore?.({
      variables: {
        cursor: data.pageInfo.endCursor ?? ""
      }
    }).then(() => {
      setLoading(false);
    });
  };

  if (totalMinutes === 0) {
    return null;
  }

  return (
    <>
      <div className={styles.title}>
        <FormattedMessage id={`pages.expert.dashboard.timeLogs.${tableIndex}`} />
      </div>
      <div className={styles.table}>
        <TimeLogTables pageInfo={data.pageInfo} refetch={refetch} skipScroll timeLogs={timeLogs} />
        {data.pageInfo.hasNextPage && (
          <div className="ion-margin-start">
            {loading ? (
              <Icon icon="spinner" spacingStart />
            ) : (
              <div>
                <ButtonLink onClick={searchNext}>
                  <FormattedMessage id="pages.expert.dashboard.timeLogs.loadMore" />
                </ButtonLink>
              </div>
            )}
          </div>
        )}
      </div>
      <div>
        <div className={styles.timeLogTotal}>
          <div className="bold">
            <FormattedMessage id="pages.expert.dashboard.timeLogs.total" />
          </div>
          <div className={styles.totalMinutes}>
            {minutesToHoursAndMinutes({
              minutes: totalMinutes,
              roundRemainder: true
            })}
          </div>
        </div>
      </div>
    </>
  );
};

const ExpertDashboardTimeLogsPage = () => {
  const location = useLocation();
  const intl = useIntl();
  const myExpert = useMyExpert();

  const type = useMemo(() => {
    if (location.pathname.includes("non-billable")) {
      return Type.NON_BILLABLE;
    }
    return Type.BILLABLE;
  }, [location.pathname]);

  const [filters, setFilters] = useState<Filters>({
    endDate: calculateDatesFromTimePeriod(TimePeriodEnum.SEVEN_DAYS, null, null, intl).endDate,
    expertId: myExpert?.expertGlobalRole === ExpertGlobalRoleEnum.MANAGER ? null : myExpert?.id,
    startDate: calculateDatesFromTimePeriod(TimePeriodEnum.SEVEN_DAYS, null, null, intl).startDate,
    timePeriod: TimePeriodEnum.SEVEN_DAYS
  });

  const handleFilterChange = useCallback(
    (newFilters: Filters) => {
      if (!equal(newFilters, filters)) {
        setFilters(newFilters);
      }
    },
    [filters]
  );

  const [getTimeLogs, getTimeLogsQuery] = useLazyQuery<GetTimeLogsByGroupQuery, GetTimeLogsByGroupQueryVariables>(
    GetTimeLogsByGroupDocument
  );

  const [getExpertOverview, getExpertOverviewQuery] = useLazyQuery<
    GetExpertOverviewQuery,
    GetExpertOverviewQueryVariables
  >(GetExpertOverviewDocument);

  getExpertOverviewQuery.data?.expertOverview.activityKindBillableMinutes;

  useEffect(() => {
    if (filters.timePeriod === TimePeriodEnum.CUSTOM && (!filters.startDate || !filters.endDate)) {
      return;
    }
    const dates = calculateDatesFromTimePeriod(filters.timePeriod, filters.startDate, filters.endDate, intl);

    if (myExpert) {
      getTimeLogs({
        variables: {
          billable:
            type === Type.BILLABLE ? TimeLogBillableStatusEnum.BILLABLE : TimeLogBillableStatusEnum.NON_BILLABLE,
          endDate: dates.endDate,
          expertId: filters.expertId,
          startDate: dates.startDate
        }
      });
      getExpertOverview({
        variables: {
          endDate: dates.endDate,
          expertId: filters.expertId,
          startDate: dates.startDate
        }
      });
    }
  }, [filters, getExpertOverview, getTimeLogs, intl, myExpert, type]);

  const getOtherGroup = () => {
    if (getExpertOverviewQuery.data) {
      if (type === Type.BILLABLE) {
        return (
          getExpertOverviewQuery.data?.expertOverview.activityKindBillableMinutes.total -
          getExpertOverviewQuery.data?.expertOverview.activityKindBillableMinutes.carePlan -
          getExpertOverviewQuery.data.expertOverview.activityKindBillableMinutes.carePlanUpdates -
          getExpertOverviewQuery.data.expertOverview.activityKindBillableMinutes.concierge -
          getExpertOverviewQuery.data.expertOverview.activityKindBillableMinutes.messaging -
          getExpertOverviewQuery.data?.expertOverview.activityKindBillableMinutes.consult -
          getExpertOverviewQuery.data?.expertOverview.activityKindBillableMinutes.documentation -
          getExpertOverviewQuery.data?.expertOverview.activityKindBillableMinutes.resource
        );
      } else {
        return (
          getExpertOverviewQuery.data?.expertOverview.activityKindNonBillableMinutes.total -
          getExpertOverviewQuery.data?.expertOverview.activityKindNonBillableMinutes.carePlan -
          getExpertOverviewQuery.data?.expertOverview.activityKindNonBillableMinutes.carePlanUpdates -
          getExpertOverviewQuery.data?.expertOverview.activityKindNonBillableMinutes.concierge -
          getExpertOverviewQuery.data?.expertOverview.activityKindNonBillableMinutes.messaging -
          getExpertOverviewQuery.data?.expertOverview.activityKindNonBillableMinutes.consult -
          getExpertOverviewQuery.data?.expertOverview.activityKindNonBillableMinutes.documentation -
          getExpertOverviewQuery.data?.expertOverview.activityKindNonBillableMinutes.resource
        );
      }
    }
    return 0;
  };

  const otherGroup = getOtherGroup();

  const totalMinutesBillableGroup =
    type === Type.BILLABLE
      ? getExpertOverviewQuery.data?.expertOverview.activityKindBillableMinutes
      : getExpertOverviewQuery.data?.expertOverview.activityKindNonBillableMinutes;

  return (
    <AppPage>
      <Helmet>
        <title>{intl.formatMessage({ id: "pageTitles.expert.dashboardTimeLogs.list" })}</title>
      </Helmet>

      <PageTitle imageName="peopleCoffeeLight" titleKey={`pages.expert.dashboard.timeLogs.${type}.title`} />

      <TertiaryButton
        icon="chevronLeft"
        routerLink={`/expert/dashboard${location.search}`}
        textKey="pages.expert.dashboard.consults.dashboard"
      />
      <div className={styles.flex}>
        <div
          className={`${styles.filters} ${filters.timePeriod === TimePeriodEnum.CUSTOM ? styles.biggerFilters : ""}`}
        >
          <ExpertKPIsFilters filters={filters} onChange={handleFilterChange} />
        </div>
        {getExpertOverviewQuery.data && (
          <div className={styles.infoBox}>
            <div className={styles.infoBoxLabel}>
              <FormattedMessage id="pages.expert.dashboard.timeLogs.total" />
            </div>
            <div className={styles.infoBoxData}>
              {minutesToHoursAndMinutes({
                minutes:
                  type === Type.BILLABLE
                    ? getExpertOverviewQuery.data.expertOverview.activityKindBillableMinutes.total
                    : getExpertOverviewQuery.data.expertOverview.activityKindNonBillableMinutes.total,
                roundRemainder: true
              })}
            </div>
          </div>
        )}
      </div>

      <HandleQuery query={getExpertOverviewQuery}>
        {_expertOverviewData => (
          <HandleQuery query={getTimeLogsQuery}>
            {data => (
              <>
                <Section
                  data={data.carePlan}
                  fetchMore={getTimeLogsQuery.fetchMore}
                  refetch={getTimeLogsQuery.refetch}
                  tableIndex={0}
                  totalMinutes={totalMinutesBillableGroup?.carePlan ?? 0}
                />
                <Section
                  data={data.carePlanUpdates}
                  fetchMore={getTimeLogsQuery.fetchMore}
                  refetch={getTimeLogsQuery.refetch}
                  tableIndex={1}
                  totalMinutes={totalMinutesBillableGroup?.carePlanUpdates ?? 0}
                />
                <Section
                  data={data.resource}
                  fetchMore={getTimeLogsQuery.fetchMore}
                  refetch={getTimeLogsQuery.refetch}
                  tableIndex={2}
                  totalMinutes={totalMinutesBillableGroup?.resource ?? 0}
                />
                <Section
                  data={data.consult}
                  fetchMore={getTimeLogsQuery.fetchMore}
                  refetch={getTimeLogsQuery.refetch}
                  tableIndex={3}
                  totalMinutes={totalMinutesBillableGroup?.consult ?? 0}
                />
                <Section
                  data={data.documentation}
                  fetchMore={getTimeLogsQuery.fetchMore}
                  refetch={getTimeLogsQuery.refetch}
                  tableIndex={4}
                  totalMinutes={totalMinutesBillableGroup?.documentation ?? 0}
                />
                <Section
                  data={data.concierge}
                  fetchMore={getTimeLogsQuery.fetchMore}
                  refetch={getTimeLogsQuery.refetch}
                  tableIndex={5}
                  totalMinutes={totalMinutesBillableGroup?.concierge ?? 0}
                />
                <Section
                  data={data.messaging}
                  fetchMore={getTimeLogsQuery.fetchMore}
                  refetch={getTimeLogsQuery.refetch}
                  tableIndex={6}
                  totalMinutes={totalMinutesBillableGroup?.messaging ?? 0}
                />
                <Section
                  data={data.other}
                  fetchMore={getTimeLogsQuery.fetchMore}
                  refetch={getTimeLogsQuery.refetch}
                  tableIndex={7}
                  totalMinutes={otherGroup}
                />
              </>
            )}
          </HandleQuery>
        )}
      </HandleQuery>
    </AppPage>
  );
};

export default ExpertDashboardTimeLogsPage;
